// SHA-256 de un archivo, calculado EN EL NAVEGADOR.
//
// El binario no pasa por la API (ADR-015): sube directo al almacenamiento y a la API
// solo llega su referencia. Eso deja la huella del lado del cliente, y por eso el
// backend la trata como un dato declarado, no como una garantia — el `CHECK` de
// `adjunto` valida el FORMATO (64 hex), no que corresponda al archivo.
//
// Aqui sirve para dos cosas concretas:
//   1. HU-07 evita subir dos veces el mismo archivo al mismo caso. Sin huella habria
//      que comparar por nombre, y "informe.pdf" es el mismo nombre en todos lados.
//   2. Da integridad de extremo a extremo: si el archivo se corrompe al subir, la
//      huella declarada deja de cuadrar con el contenido almacenado.
//
// `crypto.subtle` solo existe en contexto seguro (https o localhost). En un origen
// inseguro no esta disponible, y es preferible fallar con un mensaje claro que
// inventar una huella que pareceria valida.

import type { Adjunto, ConfiguracionDeAdjuntos } from '../tipos';

export async function sha256DeArchivo(archivo: File): Promise<string> {
  if (!globalThis.crypto?.subtle) {
    throw new Error(
      'No se puede calcular la huella del archivo: el navegador solo expone crypto.subtle '
      + 'en contexto seguro (https o localhost).',
    );
  }
  const contenido = await archivo.arrayBuffer();
  const resumen = await globalThis.crypto.subtle.digest('SHA-256', contenido);
  return [...new Uint8Array(resumen)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export const BYTES_POR_MB = 1024 * 1024;

/**
 * Bytes -> "12.4 MB". Un solo sitio, y con el MISMO formato que el servidor
 * (`_mb()` en `politica_de_adjuntos.py`, que es `f"{x:.1f} MB"`): el registrador no
 * deberia ver dos redondeos distintos del mismo archivo segun quien lo rechace.
 */
export function enMb(bytes: number | null | undefined): string {
  return `${((bytes ?? 0) / BYTES_POR_MB).toFixed(1)} MB`;
}

/**
 * BUG-380 · el archivo vacio, dicho SIN depender de la configuracion (AT S3 §2.2.7).
 *
 * Devuelve el motivo si el archivo tiene 0 bytes, o `null` si no. No mira los limites
 * de HU-31 porque no los necesita: un archivo vacio no es valido con ninguna
 * configuracion, y el servidor lo rechaza en la frontera (`Field(gt=0)`) antes de que
 * su politica de adjuntos llegue a mirarlo.
 *
 * Existe aparte de `motivosParaRechazar` porque quien abre el borrador antes de subir
 * (`RegistroConectado`) tiene que poder frenarlo ANTES de cualquier peticion, y porque la
 * seleccion multiple de BUG-377 descarta los vacios archivo por archivo antes del lote.
 * El texto es el del criterio BUG-380-C1 y el mismo que devuelve el lote en servidor;
 * los anexos de la ampliacion (`lib/anexos.ts`) dicen exactamente lo mismo.
 */
export function motivoDeArchivoVacio(archivo: File): string | null {
  // `=== 0` y no `!archivo.size`: un doble de prueba sin `size` no debe leerse como
  // "archivo vacio". Un `File` de verdad siempre lo trae.
  if (archivo.size !== 0) return null;
  return `«${archivo.name}» está vacío (0 bytes). Vuelve a exportarlo y adjúntalo de nuevo.`;
}

/**
 * Pre-validacion contra los limites de HU-31, en pantalla y antes de subir.
 *
 * Es CORTESIA, no una guarda: la validacion de verdad la hace el servidor. Sirve
 * para no hacerle esperar al usuario una subida de 30 MB que va a acabar en un 413.
 *
 * Devuelve la lista de motivos por los que NO se puede adjuntar; vacia = adelante.
 *
 * ------------------------------------------------------------------------------
 * POR QUE SE REESCRIBIO (CP-07-03, 04, 08, 09 y 11 · 4-sep-2026)
 * ------------------------------------------------------------------------------
 * El servidor ya redactaba estos mensajes EXACTAMENTE como pide HU-07 —nombra la
 * extension y aclara que cualquier otro formato si se acepta, dice cuanto queda del
 * presupuesto, y comprueba el tope por archivo antes que el total—. Esta funcion se
 * adelantaba con una version peor de cada uno, asi que el usuario nunca llegaba a
 * leer los buenos. Ahora dice lo mismo, con las mismas palabras y en el mismo orden.
 *
 * DEVUELVE UN SOLO MOTIVO, no la lista de todos los que se incumplen. HU-07 lo pide
 * asi: "si se violan los dos topes, el mensaje habla DEL ARCHIVO, que es lo que el
 * usuario puede arreglar". Antes se apilaban "pesa de mas" y "no cabe en el total",
 * y el segundo tapaba la accion concreta —comprimelo— con una decision difusa.
 *
 * EL ORDEN ES EL DEL SERVIDOR, a proposito: si los dos lados eligen distinto que
 * decir, el mensaje cambia segun donde se corte, que es justo la incoherencia que este
 * cambio viene a quitar. Primero la frontera y despues `PoliticaDeAdjuntos.verificar`.
 *
 * EL VACIO VA PRIMERO Y FUERA DE LA CONFIGURACION (BUG-380, 30-sep-2026). No lo cubre la
 * politica del dominio: lo rechaza Pydantic en la frontera (`Field(gt=0)`), antes que
 * cualquier limite, y de ahi salia un 422 con el texto de la libreria —"Input should be
 * greater than 0"—, que es lo que QA reporto como no legible. Estaba intercalado junto
 * al peso y DESPUES de `if (!configuracion) return []`: con la configuracion pendiente o
 * fallida, el vacio salia hacia el servidor y el registrador volvia a leer el texto
 * crudo. Ahora se corta aqui con o sin configuracion.
 */
export function motivosParaRechazar(
  archivo: File,
  configuracion: ConfiguracionDeAdjuntos | null | undefined,
  yaAdjuntos: Adjunto[] = [],
): string[] {
  const vacio = motivoDeArchivoVacio(archivo);
  if (vacio) return [vacio];

  // Sin configuracion no se pre-valida nada mas: el servidor sigue validando el resto.
  if (!configuracion) return [];

  const extension = (archivo.name.split('.').pop() ?? '').toLowerCase();
  if ((configuracion.extensiones_bloqueadas ?? []).includes(extension)) {
    return [
      `Los archivos .${extension} no se aceptan. Se acepta cualquier otro formato: `
      + 'documento, imagen, vídeo o comprimido.',
    ];
  }

  if (yaAdjuntos.length >= configuracion.cantidad_maxima) {
    return [
      `El caso ya tiene ${yaAdjuntos.length} evidencias y el máximo por caso es `
      + `${configuracion.cantidad_maxima}. Elimina una para adjuntar otra.`,
    ];
  }

  // En BYTES y no en MB: comparar el flotante redondeado dejaba pasar por la frontera
  // exacta cosas que el servidor rechaza, y CP-07-08 prueba precisamente la frontera.
  if (archivo.size > configuracion.tamano_maximo_mb * BYTES_POR_MB) {
    return [
      `«${archivo.name}» pesa ${enMb(archivo.size)} y el máximo por archivo es `
      + `${configuracion.tamano_maximo_mb} MB.`,
    ];
  }

  const yaUsados = yaAdjuntos.reduce((suma, a) => suma + (a.tamano_bytes ?? 0), 0);
  const topeTotal = configuracion.tamano_total_maximo_mb * BYTES_POR_MB;
  if (yaUsados + archivo.size > topeTotal) {
    return [
      `«${archivo.name}» pesa ${enMb(archivo.size)} y solo quedan `
      + `${enMb(Math.max(0, topeTotal - yaUsados))} de los `
      + `${configuracion.tamano_total_maximo_mb} MB del caso.`,
    ];
  }

  return [];
}
